import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import type { ComponentPropsWithRef, ReactNode } from 'react'

export interface DropdownItemProps extends ComponentPropsWithRef<'div'> {
    as?: 'div' | 'a' | 'button'
    href?: string
    children?: ReactNode
}

export function DropdownItem({
    as,
    href,
    children,
    className = '',
    onClick,
    onKeyDown,
    ...restProps
}: DropdownItemProps) {
    const isAnchor = as === 'a' || Boolean(href)
    const baseClassName =
        `block px-4 py-2.5 rounded-xl cursor-pointer text-gray-800 no-underline hover:no-underline hover:bg-stone-100 focus:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${className}`.trim()

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        onKeyDown?.(e as any)

        if (
            e.key === KeyboardKeyEnum.Enter ||
            e.key === KeyboardKeyEnum.Space
        ) {
            if (onClick) {
                e.preventDefault()
                onClick(e as any)
            } else if (!isAnchor) {
                const parentAnchor = (e.currentTarget as HTMLElement).closest(
                    'a',
                )

                if (parentAnchor) {
                    e.preventDefault()
                    parentAnchor.click()
                }
            }
        }
    }

    if (isAnchor) {
        return (
            <a
                role="menuitem"
                tabIndex={0}
                href={href}
                onClick={onClick as any}
                onKeyDown={handleKeyDown as any}
                {...(restProps as ComponentPropsWithRef<'a'>)}
                className={baseClassName}
            >
                {children}
            </a>
        )
    }

    return (
        <div
            role="menuitem"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            {...restProps}
            className={baseClassName}
        >
            {children}
        </div>
    )
}
